/**
 * @fileoverview InfoCard component for displaying information in a card layout with edit functionality
 * @module components/cards/InfoCard
 * @requires react
 * @requires @mui/material
 * @requires @mui/material/styles
 */

import React from "react";
import PropTypes from "prop-types";
import { Box, Divider, Button } from "@mui/material";
import { styled } from "@mui/material/styles";

/**
 * Styled button component with custom styling
 * @type {React.ComponentType<import('@mui/material').ButtonProps>}
 */
const StyledButton = styled(Button)({
  textTransform: "none",
  borderRadius: "4px",
  padding: "0 16px",
  fontSize: "0.75rem",
  fontWeight: 500,
  letterSpacing: "0.1px",
  "&.MuiButton-outlined": {
    backgroundColor: "transparent",
    border: "1px solid #4b3f72",
    color: "#4b3f72",
    "&:hover": {
      backgroundColor: "rgba(75, 63, 114, 0.04)",
      borderColor: "#4b3f72",
    },
  },
  "&.MuiButton-contained": {
    backgroundColor: "#4b3f72",
    color: "white",
    "&:hover": {
      backgroundColor: "#3a3159",
    },
  },
});

/**
 * InfoCard Component
 * A reusable card component that can display content with an optional edit mode.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.title - The title to display at the top of the card
 * @param {React.ReactNode} props.children - The content to display inside the card
 * @param {boolean} [props.isEditing=false] - Whether the card is in edit mode
 * @param {Function} [props.onEdit] - Callback when edit button is clicked
 * @param {Function} [props.onCancel] - Callback when cancel button is clicked
 * @param {Function} [props.onSave] - Callback when save button is clicked
 * @param {string} [props.editButtonText="Edit"] - Text to display on the edit button
 * @param {boolean} [props.showEditButton=true] - Whether to show the edit button
 * @param {boolean} [props.showDivider=true] - Whether to show a divider below the title
 * @param {string} [props.titleVariant="subtitle1"] - Typography variant for the title
 * @param {string} [props.contentFontSize="0.9rem"] - Font size for the content
 * @param {boolean} [props.quickview=false] - Whether the card is in quickview mode (affects styling)
 * @returns {JSX.Element} The InfoCard component
 */
const InfoCard = ({
  title,
  children,
  isEditing,
  onEdit,
  onCancel,
  onSave,
  editButtonText = "Edit",
  showEditButton = true,
  showDivider = true,
  titleVariant = "subtitle1",
  contentFontSize = "0.9rem",
  quickview = false,
  sx = {},
}) => {
  return (
    <Box
      sx={{
        height: "calc(100vh - 10%)",
        borderRadius: 2,

        backgroundColor: "white",

        overflowY: "overlay",
        ...sx,
      }}
    >
      <Box
        mb={1.5}
        sx={{ position: "absolute", width: "100%", zIndex: 100000 }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          backgroundColor="#403C6F"
          mb={1}
        >
          {showEditButton &&
            (isEditing ? (
              <Box display="flex" gap={1} alignItems="center">
                <StyledButton
                  size="small"
                  variant="outlined"
                  onClick={onCancel}
                  sx={{
                    minWidth: "50px",
                    height: "30px",
                    fontSize: "0.75rem",
                    padding: "0 12px",
                  }}
                >
                  Cancel
                </StyledButton>
                <StyledButton
                  size="small"
                  variant="contained"
                  onClick={onSave}
                  sx={{
                    minWidth: "50px",
                    height: "30px",
                    fontSize: "0.75rem",
                    padding: "0 12px",
                  }}
                >
                  Save
                </StyledButton>
              </Box>
            ) : (
              <StyledButton size="small" variant="outlined" onClick={onEdit}>
                {editButtonText}
              </StyledButton>
            ))}
        </Box>
        {showDivider && <Divider />}
      </Box>

      <Box sx={{ fontSize: contentFontSize, pl: quickview ? 2 : 2.5, mt: 2 }}>
        {children}
      </Box>
    </Box>
  );
};

InfoCard.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.node,
  isEditing: PropTypes.bool,
  onEdit: PropTypes.func,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  editButtonText: PropTypes.string,
  showEditButton: PropTypes.bool,
  showDivider: PropTypes.bool,
  titleVariant: PropTypes.string,
  contentFontSize: PropTypes.string,
  quickview: PropTypes.bool,
  sx: PropTypes.object,
};

export default InfoCard;
